import {Directive, ElementRef, HostListener, AfterViewInit, Output, EventEmitter, Input, Inject, OnDestroy} from '@angular/core';
import {fromEvent, ReplaySubject, Subject, Subscription, takeUntil, throttleTime} from 'rxjs';
import {log} from '../functions';
import {InterfaceService} from '../services/interface.service';
import {NzModalService} from 'ng-zorro-antd/modal';

/*
 * Directive to add 'drag' support to Ngx Bootstrap modals (https://github.com/valor-software/ngx-bootstrap).
 * Based on this library to enable drag support for an ng-bootstrap modal: https://github.com/mattxu-zz/ngb-modal-draggable
 *
 * Enable by adding the directive to the modal-header element, e.g.:
 *
 * <div class="modal-header" ngxModalDraggable>  </div>
 */

@Directive({
  selector: '[ngxModalDraggable]'
})
export class NgxModalDraggableDirective implements AfterViewInit, OnDestroy {
  public modalElement: HTMLElement;
  public dialogElement: HTMLElement;
  public topStart: number;
  public leftStart: number;
  public isDraggable: boolean;
  public handleElement: HTMLElement;
  public backdrop: HTMLElement;
  public isExpand: boolean;
  @Input('expand') expand = true;
  @Output() onMove$ = new EventEmitter<any>();
  @Output() modalMinimize$ = new EventEmitter<boolean>();
  @Output() modalExpand$ = new EventEmitter<boolean>();
  public heightHeader = 45;
  public savedLeftPosition: number;
  public savedTopPosition: number;
  public isTopExpand: boolean;

  clickedElement: Subscription = new Subscription();
  private destroy$: Subject<void> = new Subject();
  constructor(
    private modalService: NzModalService,
    public interfaceService: InterfaceService,
    public element: ElementRef
  ) {
    this.modalService.openModals.map((v) => v.afterClose.subscribe(() => {
      this.modalMinimizeSort();
    }));
    this.onMove$.pipe(takeUntil(this.destroy$), throttleTime(100)).subscribe(() => {
      if (this.isExpand) {
        this.modalExpand();
      }else if (this.expand){
        this.setTopExpand((this.top <= 0));
      }

    });
    this.interfaceService.resizeObservable$.pipe(takeUntil(this.destroy$), throttleTime(100)).subscribe((id) => {
      this.modalPositionFixed();
      this.modalMinimizeSort();
    });
  }
  setTopExpand(val) {
    if (val) {
      document.getElementsByTagName('app-root')[0].classList.add('topexpand');
      this.isTopExpand = true;
    } else {
      this.isTopExpand = false;
      document.getElementsByTagName('app-root')[0].classList.remove('topexpand');
    }
  }
  ngAfterViewInit() {
    setTimeout(() => {
      this.initSetModalCenter();
    }, 5);
    this.backdrop = (document.getElementsByClassName('modal-backdrop')[0] as HTMLElement);
    let element: HTMLElement = this.element.nativeElement;

    // only make the modal header draggable
    this.handleElement = this.element.nativeElement;


    // change cursor on the header
    this.handleElement.style.cursor = 'pointer';

    // get the modal parent container element: that's the element we're going to move around
    for (let level = 3; level > 0; level--) {
      element = (element.parentNode as HTMLElement);
    }
    const modal: HTMLElement = element.closest('.modal');
    modal.classList.add('in');
    modal.classList.add('modal-meteor');
    this.modalElement = modal;
    this.dialogElement = element;
    this.dialogElement.style.position = 'relative';


    this.clickedElement = fromEvent(this.modalElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.modalElement.classList.add('active');
    });
  }

  private searchParentNode(element: Node, tag: Node): Node | null {
    while (element.parentNode) {
      element = element.parentNode;
      if (element === tag) {
        return element;
      }
    }

    return null;
  }

  @HostListener('mousedown', ['$event'])
  public onMouseDown(event: MouseEvent) {
    console.log('mousedown', event);
    if (!(event.srcElement as HTMLDivElement)?.classList.contains('moved')) {
      return;
    }
    if (event.button === 2 || !this.handleElement) {
      return; // prevents right click drag or initialized handleElement
    }

    if (event.target !== this.handleElement && !this.searchParentNode(event.target as any, this.handleElement)) {
      return; // prevents dragging of other elements than children of handleElement
    }
    // enable dragging
    this.isDraggable = true;
    this.modalElement.classList.add('draggable');
    // store original position
    this.topStart = event.clientY - Number(this.modalElement.style.top.replace('px', ''));
    this.leftStart = event.clientX - Number(this.modalElement.style.left.replace('px', ''));

    // event.preventDefault();
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    this.isDraggable = false;

    this.modalElement.classList.remove('draggable');
    if (this.isTopExpand){
      this.modalExpand();
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (this.isDraggable) {
      this.removeBackDrop();
      this.onMove$.next({
        topStart: this.topStart,
        leftStart: this.leftStart,
      });
      // on moving the mouse, reposition the modal
      this.setModalPosition((event.clientY - this.topStart), (event.clientX - this.leftStart));
    }
  }

  @HostListener('document:mouseleave', ['$event'])
  onMouseLeave(event: MouseEvent) {
    this.isDraggable = false;
    this.modalPositionFixed();
  }

  public removeBackDrop(): void {
    if (this.backdrop) {
      const overlay = document.getElementsByClassName('cdk-overlay-container')[0] as HTMLElement;
      overlay?.classList?.add('hidden');
      this.backdrop?.classList?.add('hidden');
    }
  }

  public get isMinimize() {
    return this.modalElement && this.modalElement.classList.contains('minimize');
  }


  public modalMinimize() {
    if (!this.isMinimize) {
      this.savePositions();
    }else{
      this.restorePositions();
    }
    this.removeBackDrop();
    log('---', this.modalElement);
    const classList = this.modalElement.classList;
    classList.toggle('minimize');
    classList.remove('expand');
    this.modalMinimizeSort();
    this.modalMinimize$.next(classList.contains('minimize'));
  }
  public savePositions() {
    this.savedTopPosition = this.top;
    this.savedLeftPosition = this.left;
  }
  public restorePositions() {
    this.setModalPosition(this.savedTopPosition, this.savedLeftPosition);
  }
  public modalExpand() {
      this.isExpand = !this.isExpand;
      if (!this.isExpand) {
        this.setTopExpand(false);
      }
      this.removeBackDrop();
      const classList = this.modalElement.classList;
      classList.toggle('expand');
      classList.remove('minimize');
      this.modalExpand$.next(classList.contains('expand'));
  }

  initSetModalCenter() {
    if (this.modalElement) {
      const widthModal = this.modalElement.offsetWidth;
      const heightModal = this.modalElement.offsetHeight;
      const widthWindow = window.innerWidth;
      const heightWindow = window.innerHeight;
      const newLeft = (widthWindow - widthModal) / 2;
      let newTop = (heightWindow - heightModal) / 2;
      if (newTop < 10) {
        newTop = 10;
      }
      this.setModalPosition(10, newLeft);
      // console.log('MODAL_SET_CENTER_POSITION: ' + newTop + ' | ' + newLeft);
      // modalMeteor.initModalsSave();
    } else {
      log('!!!!!!!!!');
    }
    setTimeout(() => {
      this.modalElement.classList.add('init');
    }, 100);
  }

  modalMinimizeSort() {
    const widthCollapse = 135;
    const heightWindow = window.innerHeight;
    const widthWindow = window.innerWidth;

    let bottomBusy = 0;
    let count = 0;
    [].forEach.call(document.getElementsByClassName('minimize'), (modal) => {
      count++;
      bottomBusy += this.heightHeader;
      const newTop = (heightWindow - bottomBusy);
      const newLeft = (widthWindow - widthCollapse);
      modal.style.top = newTop + 'px';
      modal.style.left = newLeft + 'px';
      // console.log('MODAL_COLLAPSE_POSITION: ' + newTop + ' | ' + newLeft);
    });
    // console.log('MODAL_COLLAPSE_POSITION_COUNT: ' + count);
  }
  get left() {
    return this.modalElement.offsetLeft;
  }
  get top() {
    return this.modalElement.offsetTop;
  }
  modalPositionFixed() {
    const heightWindow = window.innerHeight;
    const widthWindow = window.innerWidth;
    // const heightModal = this.modalElement.offsetHeight;
    if (this.top < 0) {
      this.setModalPosition(0, this.left);
      // console.log('MODAL_TOP_MAX_POSITION_FIXED: ' + top + ' -> 0');
    }
    if (this.top > (heightWindow - this.heightHeader)) {
      this.setModalPosition((heightWindow - this.heightHeader), this.left);
      // console.log('MODAL_TOP_MIN_POSITION_FIXED: ' + this.top + ' -> ' + (this.heightHeader));
    }
    if ((widthWindow - this.left) < 85) {
      this.setModalPosition(this.top, (widthWindow - 85));
      // console.log('MODAL_LEFT_POSITION_FIXED');
    }
  }

  setModalPosition(top: number, left: number) {
    // this.modalElement.style.transform = `translate(${left}px, ${top}px)`;
    this.modalElement.style.top = top + 'px';
    this.modalElement.style.left = left + 'px';
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
